import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/format';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { PAYMENT_CATEGORY, type AdminBookingRow } from './types';

/**
 * How a booking was paid for, and what that means for the amount column.
 *
 * `BookingPaymentCategory` is the axis the whole page turns on: a benefit
 * booking has a value but no charge, a comped one has neither, and a
 * needs-review one has money that is not accounted for.
 */

/**
 * Destructive is reserved for "needs review" — the one category that means
 * money is unaccounted for. A comp is a decision somebody made, so it reads as
 * neutral rather than as a problem.
 */
const CATEGORY_TONE: Record<number, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'> = {
  [PAYMENT_CATEGORY.usedBenefit]: 'success',
  [PAYMENT_CATEGORY.paidOnline]: 'default',
  [PAYMENT_CATEGORY.paidOffline]: 'warning',
  [PAYMENT_CATEGORY.freeEvent]: 'secondary',
  [PAYMENT_CATEGORY.comped]: 'secondary',
  [PAYMENT_CATEGORY.awaitingPayment]: 'warning',
  [PAYMENT_CATEGORY.refunded]: 'outline',
  [PAYMENT_CATEGORY.needsReview]: 'destructive',
};

const CATEGORY_LABEL: Record<number, string> = {
  [PAYMENT_CATEGORY.usedBenefit]: 'manageBookings.category.usedBenefit',
  [PAYMENT_CATEGORY.paidOnline]: 'manageBookings.category.paidOnline',
  [PAYMENT_CATEGORY.paidOffline]: 'manageBookings.category.paidOffline',
  [PAYMENT_CATEGORY.freeEvent]: 'manageBookings.category.freeEvent',
  [PAYMENT_CATEGORY.comped]: 'manageBookings.category.comped',
  [PAYMENT_CATEGORY.awaitingPayment]: 'manageBookings.category.awaitingPayment',
  [PAYMENT_CATEGORY.refunded]: 'manageBookings.category.refunded',
  [PAYMENT_CATEGORY.needsReview]: 'manageBookings.category.needsReview',
};

export function categoryLabelKey(category: number | null | undefined) {
  if (category === null || category === undefined) {
    return CATEGORY_LABEL[PAYMENT_CATEGORY.usedBenefit];
  }
  return CATEGORY_LABEL[category] ?? CATEGORY_LABEL[PAYMENT_CATEGORY.usedBenefit];
}

/** The order the filter offers them in — money first, problems last. */
export const CATEGORY_FILTER_ORDER = [
  PAYMENT_CATEGORY.paidOnline,
  PAYMENT_CATEGORY.paidOffline,
  PAYMENT_CATEGORY.usedBenefit,
  PAYMENT_CATEGORY.freeEvent,
  PAYMENT_CATEGORY.comped,
  PAYMENT_CATEGORY.awaitingPayment,
  PAYMENT_CATEGORY.refunded,
  PAYMENT_CATEGORY.needsReview,
];

export function PaymentCategoryCell({
  row,
  language,
}: {
  row: AdminBookingRow;
  language: string;
}) {
  const { t } = useTranslation();
  const category = row.paymentCategory ?? PAYMENT_CATEGORY.usedBenefit;

  return (
    <div className="space-y-0.5">
      <Badge variant={CATEGORY_TONE[category] ?? 'secondary'}>
        {t(categoryLabelKey(category))}
      </Badge>

      {category === PAYMENT_CATEGORY.usedBenefit && (
        <p className="text-muted-foreground text-xs">
          {language === 'en' ? row.subscriptionNameEn : row.subscriptionNameEs}
        </p>
      )}

      {/* A comp is only trustworthy if you can see why. */}
      {category === PAYMENT_CATEGORY.comped && row.compedReason && (
        <p className="text-muted-foreground text-xs">{row.compedReason}</p>
      )}

      {row.hasOfflinePayment && (row.paymentMethods?.length ?? 0) > 0 && (
        <p className="text-muted-foreground text-xs">
          {row.paymentMethods!
            .map((method) => t(paymentMethodLabelKey(method)))
            .join(', ')}
        </p>
      )}
    </div>
  );
}

/**
 * The amount, which is a different statement per category — a benefit shows a
 * value rather than a charge, a comp shows no charge at all, and a
 * needs-review row says outright that the money is unaccounted for.
 */
export function BookingAmount({ row }: { row: AdminBookingRow }) {
  const { t } = useTranslation();
  const category = row.paymentCategory ?? PAYMENT_CATEGORY.usedBenefit;

  if (category === PAYMENT_CATEGORY.usedBenefit) {
    return (
      <span className="text-muted-foreground tabular-nums">
        {formatCurrency(row.price)} {t('manageBookings.value')}
      </span>
    );
  }

  if (category === PAYMENT_CATEGORY.freeEvent) {
    return (
      <span className="text-muted-foreground">
        {t('manageBookings.freeOfCharge')}
      </span>
    );
  }

  if (category === PAYMENT_CATEGORY.comped) {
    return (
      <span className="text-muted-foreground">
        {t('manageBookings.noCharge')}
      </span>
    );
  }

  if (category === PAYMENT_CATEGORY.needsReview) {
    return (
      <div>
        <p className="text-destructive font-semibold tabular-nums">
          {formatCurrency(row.price)}
        </p>
        <p className="text-destructive text-xs">
          {t('manageBookings.unaccountedFor')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="font-semibold tabular-nums">{formatCurrency(row.paid)}</p>
      {(row.outstanding ?? 0) > 0 && (
        <p className="text-warning text-xs tabular-nums">
          {formatCurrency(row.outstanding)}{' '}
          {t('manageBookings.outstanding').toLowerCase()}
        </p>
      )}
      {(row.refunded ?? 0) > 0 && (
        <p className="text-muted-foreground text-xs tabular-nums">
          {t('manageBookings.refunded')} {formatCurrency(row.refunded)}
        </p>
      )}
    </div>
  );
}
